import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Squad, SquadMember } from '../types';
import { User, Mail, Phone, Calendar, Trophy, Crosshair, TrendingUp, Edit, Users, X, Settings, Check, Clock, Menu, Search, Loader, XCircle, Send, LogOut, Upload } from 'lucide-react';
import { useModal } from '../context/ModalContext';
import { useAuth } from '../context/AuthContext';
import { checkUsernameUnique, checkUserInSquad, getUserByUsername } from '../utils/userUtils';
import { uploadProfilePicture } from '../services/storageService';
import AvatarDisplay from '../components/AvatarDisplay';
import Cropper from 'react-easy-crop';
import getCroppedImg, { type PixelCrop } from '../utils/cropImage';
import AddIgnModal from '../components/AddIgnModal';
import { doc, collection, query, where, onSnapshot, updateDoc } from 'firebase/firestore';
import { db, functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import TCoinIcon from '../components/TCoinIcon';
import PresenceBadge from '../components/PresenceBadge';
import LoadingOverlay from '../components/LoadingOverlay';

export default function Profile() {
    const { showAlert } = useModal();
    const { user, updateUserContext, logout } = useAuth();
    const navigate = useNavigate();
    const [globalLoadingText, setGlobalLoadingText] = useState<string | null>(null);
    const [profileData, setProfileData] = useState<any>({
        stats: { tournamentsPlayed: 0, wins: 0, totalWinnings: 0, winRate: 0 },
        igns: []
    });
    const p = profileData;

    useEffect(() => {
        if (!user) return;

        const unsubscribe = onSnapshot(doc(db, 'users', user.uid), (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                setProfileData((prev: any) => ({
                    ...prev,
                    ...data,
                    // Explicitly map nested data correctly when updating from firestore if possible
                    uid: docSnap.id,
                    username: data.username || user.displayName || prev.username,
                    avatar: data.avatar || user.photoURL || prev.avatar,
                    email: data.email || user.email || prev.email || '',
                    phone: data.phone || user.phoneNumber || prev.phone || '',
                    joinedAt: data.joinedAt || user.metadata?.creationTime || prev.joinedAt
                }));
            } else {
                setProfileData((prev: any) => ({
                    ...prev,
                    username: user.displayName || prev.username,
                    avatar: user.photoURL || prev.avatar,
                    uid: user.uid,
                    email: user.email || prev.email || '',
                    phone: user.phoneNumber || prev.phone || '',
                    joinedAt: user.metadata?.creationTime || prev.joinedAt
                }));
            }
        });
        return () => unsubscribe();
    }, [user?.uid, user?.displayName, user?.photoURL]);
    const [squads, setSquads] = useState<Squad[]>([]);
    const [showSquadModal, setShowSquadModal] = useState(false);
    const [isManagingMember, setIsManagingMember] = useState(false);

    useEffect(() => {
        if (!user) return;
        
        const q = query(collection(db, 'squads'), where('memberUids', 'array-contains', user.uid));
        const unsubSquads = onSnapshot(q, (snapshot) => {
            if (snapshot.docs.length === 0) {
                setSquads([]);
                return;
            }

            const results: Squad[] = snapshot.docs.map(squadDoc => {
                const sqId = squadDoc.id;
                const sqData = squadDoc.data();
                return {
                    id: sqId,
                    name: sqData.name,
                    game: sqData.game,
                    status: sqData.status,
                    nameLastChanged: sqData.nameLastChanged,
                    members: sqData.members || []
                } as Squad;
            });

            setSquads(results);
        });

        return () => unsubSquads();
    }, [user?.uid]);
    
    // Custom Modals State
    const [showAvatarModal, setShowAvatarModal] = useState(false);
    const [showPersonalInfoModal, setShowPersonalInfoModal] = useState(false);
    
    // File upload state for avatar
    const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
    const [avatarUploadProgress, setAvatarUploadProgress] = useState(0);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Cropping state
    const [selectedImageForCrop, setSelectedImageForCrop] = useState<string | null>(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<PixelCrop | null>(null);

    const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            const reader = new FileReader();
            reader.addEventListener('load', () => setSelectedImageForCrop(reader.result?.toString() || null));
            reader.readAsDataURL(file);
        }
    };

    const handleCropComplete = useCallback((_croppedArea: any, croppedAreaPixels: PixelCrop) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const uploadCroppedAvatar = async () => {
        if (!selectedImageForCrop || !croppedAreaPixels || !user) return;
        
        setIsUploadingAvatar(true);
        setAvatarUploadProgress(0);
        
        try {
            const croppedImageBlob = await getCroppedImg(selectedImageForCrop, croppedAreaPixels);
            if (!croppedImageBlob) throw new Error("Failed to generate cropped image");
            
            const file = new File([croppedImageBlob], "avatar.webp", { type: "image/webp" });
            
            const downloadUrl = await uploadProfilePicture(user.uid, file, (progress) => {
                setAvatarUploadProgress(progress);
            });
            
            await updateUserContext(undefined, downloadUrl);
            await updateDoc(doc(db, 'users', user.uid), { photoURL: downloadUrl });

            setSelectedImageForCrop(null);
            setShowAvatarModal(false);
            showAlert('Profile picture updated successfully!', 'Success');
        } catch (error) {
            console.error("Profile picture upload error:", error);
            showAlert('Failed to upload profile picture. Please try again.', 'Error');
        } finally {
            setIsUploadingAvatar(false);
            setAvatarUploadProgress(0);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };
    
    // Custom Modals State for IGN
    const [showAddIgnModal, setShowAddIgnModal] = useState(false);

    // Personal Info Form State
    const [tempUsername, setTempUsername] = useState(profileData.username);
    const [tempSecEmail, setTempSecEmail] = useState(profileData.secondaryEmail || '');
    const [tempSecPhone, setTempSecPhone] = useState(profileData.secondaryPhone || '');
    const [tempPrimaryPhone, setTempPrimaryPhone] = useState(profileData.phone || '');
    const [showAddSecEmail, setShowAddSecEmail] = useState(false);
    const [showAddSecPhone, setShowAddSecPhone] = useState(false);

    const [kickOutMember, setKickOutMember] = useState<{ squadId: string, memberName: string, memberUid?: string } | null>(null);
    const [kickOutReason, setKickOutReason] = useState('Did not meet squad requirements.');
    const [leaveSquad, setLeaveSquad] = useState<{ squadId: string, squadName: string } | null>(null);
    const [leaveReason, setLeaveReason] = useState('Personal reasons.');
    
    // Squad Form State
    const [editSquadId, setEditSquadId] = useState<string | null>(null);
    const [sqGame, setSqGame] = useState<'PUBG' | 'Free Fire' | 'Wild Rift' | 'MLBB'>('PUBG');
    const [sqName, setSqName] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [expandedSquadId, setExpandedSquadId] = useState<string | null>(null);

    // Phase 4 Settings State
    const [nameChangeInput, setNameChangeInput] = useState('');
    const [coLeaderAssignTarget, setCoLeaderAssignTarget] = useState<{ squad: Squad, member: SquadMember } | null>(null);
    const [coLeaderSuccessMsg, setCoLeaderSuccessMsg] = useState<string | null>(null);
    const [showInviteSection, setShowInviteSection] = useState(false);

    const handleSaveSquadName = async (sq: Squad, newName: string) => {
        if (!newName || newName.trim() === sq.name) return;
        
        try {
            const updateFn = httpsCallable(functions, 'updateSquad');
            await updateFn({
                squadId: sq.id,
                squadName: newName.trim()
            });
            showAlert(`Your squad name has been changed to ${newName.trim()}`, 'Success');
        } catch (error: any) {
            showAlert(`Error updating squad name: ${error.message}`, 'Error');
        }
    };

    const handleCoLeaderSelect = (sq: Squad, memberUid: string) => {
        const member = sq.members.find(m => m.uid === memberUid);
        if (member) {
            setCoLeaderAssignTarget({ squad: sq, member });
        }
    };

    const confirmCoLeader = () => {
        if (!coLeaderAssignTarget) return;
        const { squad, member } = coLeaderAssignTarget;
        
        setSquads(squads.map(s => {
            if (s.id === squad.id) {
                return {
                    ...s,
                    members: s.members.map(m => {
                        if (m.uid === member.uid) return { ...m, role: 'co-leader' as const };
                        if (m.role === 'co-leader' && m.uid !== member.uid) return { ...m, role: 'member' as const }; // Demote existing co-leader
                        return m;
                    })
                };
            }
            return s;
        }));
        
        setCoLeaderSuccessMsg(`${member.username} has been selected as a co leader.`);
        showAlert(`Notification sent to ${member.username}: Congratulations, you have been selected as the co-leader of ${squad.name}`, 'Notification');
        setCoLeaderAssignTarget(null);
    };

    // Slot types (used by inline invite system)
    type SlotState = 'empty' | 'searching' | 'available' | 'pending' | 'accepted' | 'unavailable' | 'not-found';
    interface SlotData {
        state: SlotState;
        username: string;
        uid: string;
        photoURL?: string;
        searchInput: string;
        showSearch: boolean;
        hasSearched: boolean;
        isSlidingOut?: boolean;
    }
    const emptySlot = (): SlotData => ({ state: 'empty', username: '', uid: '', photoURL: '', searchInput: '', showSearch: false, hasSearched: false, isSlidingOut: false });

    const getSlotCount = (game: string) => (game === 'Wild Rift' || game === 'MLBB') ? 5 : 4;
    const getSlotLabels = (game: string): string[] => {
        if (game === 'Wild Rift' || game === 'MLBB') return ['⚡ Mid', '🌿 Jungle', '🗡️ Top', '🎯 ADC', '💚 Support'];
        return ['Player 1', 'Player 2', 'Player 3', 'Player 4'];
    };

    // Inline invite slots state — keyed by squad ID
    const [inlineSlots, setInlineSlots] = useState<Record<string, { main: SlotData[], sub: SlotData, coLeaderIdx: number }>>({}); 

    const getOrInitInlineSlots = (sq: Squad) => {
        if (inlineSlots[sq.id]) return inlineSlots[sq.id];
        const count = getSlotCount(sq.game);
        const existingMain = sq.members.filter(m => m.role !== 'substitute' && m.role !== 'leader');
        const existingSub = sq.members.find(m => m.role === 'substitute');
        const slots: SlotData[] = Array.from({ length: count - 1 }, (_, i) => {
            const m = existingMain[i];
            if (m) return { state: (m.status === 'accepted' ? 'accepted' : 'pending') as SlotState, username: m.username, uid: m.uid, searchInput: m.username, showSearch: false, hasSearched: false };
            return emptySlot();
        });
        const sub = existingSub ? { state: (existingSub.status === 'accepted' ? 'accepted' : 'pending') as SlotState, username: existingSub.username, uid: existingSub.uid, searchInput: existingSub.username, showSearch: false, hasSearched: false } : emptySlot();
        const coIdx = existingMain.findIndex(m => m.role === 'co-leader');
        const init = { main: slots, sub, coLeaderIdx: coIdx };
        setInlineSlots(prev => ({ ...prev, [sq.id]: init }));
        return init;
    };

    const updateInlineSlot = (squadId: string, slotIndex: number, updater: (s: SlotData) => SlotData) => {
        setInlineSlots(prev => {
            const entry = prev[squadId];
            if (!entry) return prev;
            return { ...prev, [squadId]: { ...entry, main: entry.main.map((s, i) => i === slotIndex ? updater(s) : s) } };
        });
    };

    const updateInlineSubSlot = (squadId: string, updater: (s: SlotData) => SlotData) => {
        setInlineSlots(prev => {
            const entry = prev[squadId];
            if (!entry) return prev;
            return { ...prev, [squadId]: { ...entry, sub: updater(entry.sub) } };
        });
    };

    const handleInlineSearch = async (squadId: string, slotIndex: number, isSub: boolean, game: string) => {
        const entry = inlineSlots[squadId];
        if (!entry) return;
        const slot = isSub ? entry.sub : entry.main[slotIndex];
        const username = slot.searchInput.trim();
        if (!username) return;

        const setSearching = (s: SlotData): SlotData => ({ ...s, state: 'searching', hasSearched: true });
        if (isSub) updateInlineSubSlot(squadId, setSearching);
        else updateInlineSlot(squadId, slotIndex, setSearching);

        if (username === p.username) {
            const setUnavail = (s: SlotData): SlotData => ({ ...s, state: 'unavailable', username, hasSearched: true });
            if (isSub) updateInlineSubSlot(squadId, setUnavail);
            else updateInlineSlot(squadId, slotIndex, setUnavail);
            showAlert('You cannot invite yourself!', 'Invalid');
            return;
        }

        const allNames = [...entry.main.map(s => s.username), entry.sub.username].filter(Boolean);
        if (allNames.includes(username)) {
            const setUnavail = (s: SlotData): SlotData => ({ ...s, state: 'unavailable', username, hasSearched: true });
            if (isSub) updateInlineSubSlot(squadId, setUnavail);
            else updateInlineSlot(squadId, slotIndex, setUnavail);
            showAlert(`${username} is already in another slot!`, 'Duplicate');
            return;
        }

        const foundUser = await getUserByUsername(username);
        if (!foundUser) {
            const setNF = (s: SlotData): SlotData => ({ ...s, state: 'not-found', username, hasSearched: true });
            if (isSub) updateInlineSubSlot(squadId, setNF);
            else updateInlineSlot(squadId, slotIndex, setNF);
            return;
        }

        const { uid, photoURL: foundPhotoURL, displayName, igns } = foundUser;
        const matchingIgn = igns.find((ign: any) => ign.game === game);
        const displayPlayerName = matchingIgn ? matchingIgn.name : displayName;

        const inSquad = await checkUserInSquad(uid, game);
        if (inSquad) {
            const setUnavail = (s: SlotData): SlotData => ({ ...s, state: 'unavailable', username: displayPlayerName, uid, photoURL: foundPhotoURL, hasSearched: true });
            if (isSub) updateInlineSubSlot(squadId, setUnavail);
            else updateInlineSlot(squadId, slotIndex, setUnavail);
            return;
        }

        const setAvail = (s: SlotData): SlotData => ({ ...s, state: 'available', username: displayPlayerName, uid, photoURL: foundPhotoURL, hasSearched: true });
        if (isSub) updateInlineSubSlot(squadId, setAvail);
        else updateInlineSlot(squadId, slotIndex, setAvail);
    };

    const handleInlineInvite = async (squadId: string, slotIndex: number, isSub: boolean) => {
        const sq = squads.find(s => s.id === squadId);
        if (!sq) return;
        const entry = inlineSlots[squadId];
        if (!entry) return;
        
        const slot = isSub ? entry.sub : entry.main[slotIndex];
        const labels = getSlotLabels(sq.game);
        const slotLabel = isSub ? 'Sub' : labels[slotIndex + 1] || `Player ${slotIndex + 2}`;

        const setPending = (s: SlotData): SlotData => ({ ...s, state: 'pending' });
        if (isSub) updateInlineSubSlot(squadId, setPending);
        else updateInlineSlot(squadId, slotIndex, setPending);

        setGlobalLoadingText(`Inviting ${slot.username}...`);

        try {
            const inviteFn = httpsCallable(functions, 'inviteSquadMember');
            await inviteFn({
                squadId,
                targetUid: slot.uid,
                slotLabel,
                isSub
            });
            showAlert(`Invitation sent to ${slot.username}!`, 'Invite Sent');
        } catch (error: any) {
            showAlert(`Failed to send invite: ${error.message}`, 'Error');
            if (isSub) clearInlineSlot(squadId, slotIndex, true);
            else clearInlineSlot(squadId, slotIndex, false);
        } finally {
            setGlobalLoadingText(null);
        }
    };

    const clearInlineSlot = (squadId: string, slotIndex: number, isSub: boolean) => {
        if (isSub) updateInlineSubSlot(squadId, () => emptySlot());
        else updateInlineSlot(squadId, slotIndex, () => emptySlot());
    };

    const propagateUsernameChange = (oldUsername: string, newUsername: string, uid: string) => {
        setSquads(prev => prev.map(sq => {
            const hasMember = sq.members.some(m => m.uid === uid);
            if (!hasMember) return sq;
            const isUserLeader = sq.members.some(m => m.role === 'leader' && m.uid === uid);
            if (!isUserLeader) {
                const leader = sq.members.find(m => m.role === 'leader');
                console.log(`[Notification to ${leader?.username}]: Member ${oldUsername} changed their name to ${newUsername}`);
                showAlert(`A notification was simulated for your squad leader (${leader?.username}).`, 'Leader Notified');
            }
            return {
                ...sq,
                members: sq.members.map(m => m.uid === uid ? { ...m, username: newUsername } : m)
            };
        }));
    };

    const openCreateModal = () => {
        setEditSquadId(null);
        setSqGame('PUBG');
        setSqName('');
        setShowSquadModal(true);
    };

    const openEditModal = (squad: Squad) => {
        setEditSquadId(squad.id);
        setSqGame(squad.game as any);
        setSqName(squad.name);
        setShowSquadModal(true);
    };

    // Old handleSlotSearch and clearSlot removed — replaced by handleInlineSearch/clearInlineSlot

    // Handle game change
    const handleGameChange = (game: 'PUBG' | 'Free Fire' | 'Wild Rift' | 'MLBB') => {
        setSqGame(game);
    };

    const handleSaveSquad = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!editSquadId && squads.some(s => s.game === sqGame)) {
            showAlert(`You are already in a squad for ${sqGame}!`, 'Squad Error');
            return;
        }

        if (!sqName.trim()) {
            showAlert('Please enter a squad name.', 'Missing Name');
            return;
        }

        setIsCreating(true);
        setGlobalLoadingText('Creating squad...');

        try {
            if (editSquadId) {
                // Update existing squad via Cloud Function
                const updateFn = httpsCallable(functions, 'updateSquad');
                await updateFn({
                    squadId: editSquadId,
                    squadName: sqName
                });
                showAlert(`Squad name updated to "${sqName}".`, 'Success');
            } else {
                // Create new squad via Cloud Function
                const createFn = httpsCallable(functions, 'createSquad');
                await createFn({
                    squadName: sqName,
                    game: sqGame,
                    mainMembers: [],
                });
                showAlert(`Squad "${sqName}" created! Now invite members from the squad card below.`, 'Squad Created');
            }
            setShowSquadModal(false);
        } catch (error: any) {
            // Extract the actual error message from Firebase errors
            const errorMessage = error?.message || 'Unknown error occurred';
            const errorDetails = error?.code || '';
            showAlert(`Error saving squad: ${errorMessage}`, 'Error');
            console.error('Squad creation error:', { errorMessage, errorDetails, fullError: error });
        } finally {
            setIsCreating(false);
            setGlobalLoadingText(null);
        }
    };

    const handleKickMember = (squadId: string, memberName: string, memberUid?: string) => {
        setKickOutMember({ squadId, memberName, memberUid });
        setKickOutReason('Did not meet squad requirements.');
    };

    const confirmKickMember = async () => {
        if (!kickOutMember) return;

        if (kickOutMember.memberUid) {
            setIsManagingMember(true);
            setGlobalLoadingText('Removing member...');
            try {
                const manageFn = httpsCallable(functions, 'manageSquadMember');
                await manageFn({
                    squadId: kickOutMember.squadId,
                    action: 'kick',
                    targetUid: kickOutMember.memberUid,
                    reason: kickOutReason
                });
                
                showAlert(`Removed ${kickOutMember.memberName} successfully.`, 'Member Removed');
            } catch (error: any) {
                showAlert(`Failed to kick member: ${error.message}`, 'Error');
            } finally {
                setIsManagingMember(false);
                setGlobalLoadingText(null);
                setKickOutMember(null);
                setInlineSlots(prev => { const next = { ...prev }; delete next[kickOutMember.squadId]; return next; });
            }
        } else {
            showAlert('Cannot kick member: missing UID.', 'Error');
            setKickOutMember(null);
        }
    };

    const handleLeaveSquad = (squadId: string, squadName: string) => {
        setLeaveSquad({ squadId, squadName });
        setLeaveReason('Personal reasons.');
    };

    const confirmLeaveSquad = async () => {
        if (!leaveSquad) return;
        
        setIsManagingMember(true);
        setGlobalLoadingText('Leaving squad...');
        try {
            const manageFn = httpsCallable(functions, 'manageSquadMember');
            await manageFn({
                squadId: leaveSquad.squadId,
                action: 'leave',
                reason: leaveReason
            });
            
            showAlert(`You have successfully left ${leaveSquad.squadName}.`, 'Left Squad');
        } catch (error: any) {
            showAlert(`Failed to leave squad: ${error.message}`, 'Error');
        } finally {
            setIsManagingMember(false);
            setGlobalLoadingText(null);
            setLeaveSquad(null);
        }
    };

    return (
        <div className="page-wrapper" style={{ maxWidth: 720 }}>
            {globalLoadingText && <LoadingOverlay text={globalLoadingText} />}
            <h1 style={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: '1.8rem', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 12 }} className="animate-in">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--accent-gradient)', color: 'white', padding: '8px', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-neon)' }}>
                    <User size={24} />
                </div>
                My Profile
            </h1>
            <p style={{ color: 'var(--text-muted)', marginBottom: 24 }} className="animate-in">Manage your account and in-game names</p>

            {/* Profile Header */}
            <div className="card animate-in" style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                <AvatarDisplay avatar={p.avatar} username={p.username} size={72} />
                <div style={{ flex: 1, minWidth: 0, paddingRight: 4 }}>
                    <h2 style={{ fontFamily: 'Outfit', fontWeight: 800, wordBreak: 'break-word', lineHeight: 1.2, marginBottom: 4 }}>{p.username}</h2>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 2 }}>Player ID: <strong style={{color: 'var(--text-primary)'}}>{p.playerId || 'N/A'}</strong></p>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Member since {p.joinedAt ? (p.joinedAt?.toDate?.() || new Date(p.joinedAt)).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'recently'}</p>
                </div>
                <button className="btn btn-secondary btn-sm" style={{ flexShrink: 0 }} onClick={() => setShowAvatarModal(true)}><Edit size={14} /> Edit</button>
            </div>

            {/* Stats */}
            <div className="responsive-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
                {[
                    { icon: <Trophy size={18} />, label: 'Played', value: p.stats?.tournamentsPlayed || 0, color: 'var(--accent-hover)' },
                    { icon: <Crosshair size={18} />, label: 'Wins', value: p.stats?.wins || 0, color: 'var(--success)' },
                    { icon: <TrendingUp size={18} />, label: 'Winnings', value: <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}><TCoinIcon size={18} /> {(p.stats?.totalWinnings || 0).toLocaleString()}</span>, color: 'var(--neon-blue)' },
                    { icon: <Trophy size={18} />, label: 'Win Rate', value: `${p.stats?.winRate || 0}%`, color: 'var(--warning)' },
                ].map((s, i) => (
                    <div key={i} className={`stat-card animate-in stagger-${i + 1}`} style={{ textAlign: 'center' }}>
                        <div style={{ color: s.color, marginBottom: 8, display: 'flex', justifyContent: 'center' }}>{s.icon}</div>
                        <div className="stat-label">{s.label}</div>
                        <div className="stat-value" style={{ fontSize: '1.3rem', color: s.color }}>{s.value}</div>
                    </div>
                ))}
            </div>

            {/* Personal Info */}
            <div className="card animate-in stagger-2" style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={{ fontFamily: 'Outfit', fontWeight: 700 }}>Personal Info</h3>
                    <button className="btn btn-secondary btn-sm" onClick={() => { 
                        setTempUsername(p.username);
                        setTempPrimaryPhone(p.phone || '');
                        setTempSecEmail(p.secondaryEmail || ''); 
                        setTempSecPhone(p.secondaryPhone || ''); 
                        setShowAddSecEmail(false);
                        setShowAddSecPhone(false);
                        setShowPersonalInfoModal(true); 
                    }}><Edit size={14} /> Edit</button>
                </div>
                {[
                    { icon: <User size={16} />, label: 'Username', value: p.username },
                    { icon: <Mail size={16} />, label: 'Email', value: p.email },
                    ...(p.secondaryEmail ? [{ icon: <Mail size={16} />, label: 'Secondary Email', value: p.secondaryEmail }] : []),
                    { icon: <Phone size={16} />, label: 'Phone', value: p.phone },
                    ...(p.secondaryPhone ? [{ icon: <Phone size={16} />, label: 'Secondary Phone', value: p.secondaryPhone }] : []),
                    { icon: <Calendar size={16} />, label: 'Joined', value: p.joinedAt ? (p.joinedAt?.toDate?.() || new Date(p.joinedAt)).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'recently' },
                ].map((f, i, arr) => (
                    <div key={i} style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0',
                        borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none',
                    }}>
                        <div style={{ color: 'var(--text-muted)' }}>{f.icon}</div>
                        <div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{f.label}</div>
                            <div style={{ fontWeight: 500 }}>{f.value}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Squads */}
            <div className="card animate-in stagger-3" style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={{ fontFamily: 'Outfit', fontWeight: 700 }}><Users size={20} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8 }}/> My Squads</h3>
                    <button className="btn btn-primary btn-sm" onClick={openCreateModal}>+ Create Squad</button>
                </div>
                
                {squads.length === 0 ? (
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', textAlign: 'center', padding: '20px 0' }}>You haven't joined or created any squads yet.</p>
                ) : (
                    squads.map(sq => (
                        <div key={sq.id} style={{
                            padding: '16px', borderRadius: 'var(--radius-md)', marginBottom: 12,
                            background: 'var(--bg-glass)', border: '1px solid var(--border)'
                        }}>

                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <span className={`tc-game-badge ${sq.game === 'PUBG' ? 'pubg' : sq.game === 'Free Fire' ? 'freefire' : 'wildrift'}`}>{sq.game}</span>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <h4 style={{ fontSize: '1.1rem', margin: 0 }}>{sq.name}</h4>
                                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>ID: {sq.id}</span>
                                    </div>
                                    <button className="btn btn-ghost btn-sm" style={{ padding: '4px', height: 'auto', minHeight: 0, color: 'var(--text-muted)' }} onClick={() => setExpandedSquadId(expandedSquadId === sq.id ? null : sq.id)}>
                                        <Menu size={15} />
                                    </button>
                                </div>
                                <span className={`badge ${sq.members.some(m => m.uid === p.uid && m.role === 'leader') ? 'live' : 'completed'}`}><span className="badge-dot"></span>{sq.members.find(m => m.uid === p.uid)?.role === 'leader' ? 'Leader' : sq.members.find(m => m.uid === p.uid)?.role === 'co-leader' ? 'Co-Leader' : 'Member'}</span>
                            </div>
                            
                            {/* Expanded Details */}
                            <div className={`expand-dropdown ${expandedSquadId === sq.id ? 'open' : ''}`}>
                                <div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                        <h5 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '0.9rem' }}>Squad Members</h5>
                                        <button className="btn btn-secondary btn-sm" style={{ padding: '4px 8px', height: 'auto', minHeight: 0 }} onClick={() => openEditModal(sq)}>
                                                                            <Settings size={14} style={{ marginRight: 6 }}/> Settings
                                                                        </button>
                                    </div>
                                    {/* Existing accepted/pending members */}
                                    <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                                            {sq.members.filter(m => m.role !== 'substitute').map((m, idx) => (
                                                                                <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(145deg, rgba(255,255,255,0.05) 0%, rgba(0,0,0,0.2) 100%)', boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.05), 0 2px 4px rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.05)', padding: '8px 12px', borderRadius: 6 }}>
                                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                                                        <AvatarDisplay avatar={m.photoURL || null} username={m.username} size={28} />
                                                                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                                                        <span title={m.status === 'accepted' ? 'Accepted' : 'Pending'} style={{ display: 'flex', alignItems: 'center', fontWeight: 700, color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.8)' }}>
                                                                                            {m.username} {m.status === 'accepted' ? <Check size={14} color="var(--success)" style={{ marginLeft: 6 }} /> : <Clock size={14} color="var(--warning)" style={{ marginLeft: 6 }} />}
                                                                                        </span>
                                                                                        <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>IGN: {m.ign}</span>
                                                                                        {m.gameId && <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>ID: {m.gameId}</span>}
                                                                                    </div>
                                                                                    </div>
                                                                                    <div>
                                                                                        <PresenceBadge uid={m.uid} />
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>

                                    {/* Inline Invite Slots — show whenever leader has open slots (any squad status) */}
                                    {sq.members.some(m => m.uid === p.uid && m.role === 'leader') && (() => {
                                        const totalSlots = getSlotCount(sq.game);
                                        const currentMainMembers = sq.members.filter(m => m.role !== 'substitute').length;
                                        const hasSub = sq.members.some(m => m.role === 'substitute');
                                        const hasOpenSlots = currentMainMembers < totalSlots || !hasSub;
                                        if (!hasOpenSlots) return null;
                                        const entry = getOrInitInlineSlots(sq);
                                        const labels = getSlotLabels(sq.game);
                                        return (
                                            <div className="inline-roster">
                                                <strong style={{ fontSize: '0.78rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Invite Members</strong>
                                                {entry.main.map((slot, idx) => {
                                                    // Skip slots already filled by existing squad members
                                                    const existingNonLeader = sq.members.filter(m => m.role !== 'substitute' && m.role !== 'leader');
                                                    if (idx < existingNonLeader.length && (slot.state === 'accepted' || slot.state === 'pending')) return null;
                                                    
                                                    const label = labels[idx + 1] || `Player ${idx + 2}`;
                                                    return (
                                                        <div key={`invite-${idx}`} className={`roster-slot slot-${slot.state}`}>
                                                            <div className="slot-avatar">
                                                                {slot.state === 'empty' && (
                                                                    <button 
                                                                        type="button" 
                                                                        className={`slot-toggle-btn ${slot.showSearch ? 'is-open' : ''}`}
                                                                        onClick={() => {
                                                                            if (!slot.showSearch) {
                                                                                updateInlineSlot(sq.id, idx, s => ({ ...s, showSearch: true, isSlidingOut: false }));
                                                                            } else {
                                                                                updateInlineSlot(sq.id, idx, s => ({ ...s, isSlidingOut: true }));
                                                                                setTimeout(() => clearInlineSlot(sq.id, idx, false), 300);
                                                                            }
                                                                        }}
                                                                    >+</button>
                                                                )}
                                                                {slot.state === 'searching' && <Loader size={20} className="inline-spinner" />}
                                                                {(slot.state === 'available' || slot.state === 'pending' || slot.state === 'accepted') && <AvatarDisplay avatar={slot.photoURL || null} username={slot.username} size={28} />}
                                                                {(slot.state === 'unavailable' || slot.state === 'not-found') && <XCircle size={20} />}
                                                            </div>
                                                            <div className="slot-info">
                                                                <span className={`slot-label ${slot.showSearch ? 'slot-content-up' : ''}`}>{label}</span>
                                                                {slot.state === 'empty' && !slot.showSearch && <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>Click + to search</span>}
                                                                {slot.state === 'searching' && <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>Searching...</span>}
                                                                {slot.state === 'not-found' && <span style={{ fontSize: '0.82rem', color: 'var(--danger)' }}>"{slot.searchInput}" not found</span>}
                                                                {slot.state === 'unavailable' && <span style={{ fontSize: '0.82rem', color: 'var(--danger)' }}>{slot.username} — in another squad</span>}
                                                                {slot.state === 'available' && <span className="slot-username slot-result-found" style={{ color: 'var(--success)' }}>{slot.username} ✓</span>}
                                                                {slot.state === 'pending' && <span className="slot-username" style={{ color: 'var(--warning)' }}>{slot.username} — Pending</span>}
                                                                
                                                                {/* Animated search row */}
                                                                {(slot.showSearch || slot.state === 'not-found' || slot.state === 'unavailable' || slot.state === 'available') && slot.state !== 'searching' && (
                                                                    <div className={`inline-search-row ${slot.isSlidingOut ? 'sliding-out' : 'sliding-in'}`}>
                                                                        <input 
                                                                            placeholder="Username" 
                                                                            value={slot.searchInput} 
                                                                            onChange={e => updateInlineSlot(sq.id, idx, s => ({ ...s, searchInput: e.target.value, hasSearched: false }))} 
                                                                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleInlineSearch(sq.id, idx, false, sq.game); }}} 
                                                                        />
                                                                        <button 
                                                                            type="button" 
                                                                            className={`search-btn ${slot.hasSearched ? 'is-cancel' : ''}`} 
                                                                            onClick={() => {
                                                                                if (slot.hasSearched) {
                                                                                    updateInlineSlot(sq.id, idx, s => ({ ...s, isSlidingOut: true }));
                                                                                    setTimeout(() => clearInlineSlot(sq.id, idx, false), 300);
                                                                                } else { handleInlineSearch(sq.id, idx, false, sq.game); }
                                                                            }}
                                                                        >
                                                                            {slot.hasSearched ? <X size={14} /> : <Search size={14} />}
                                                                        </button>
                                                                        {slot.state === 'available' && (
                                                                            <button type="button" className="invite-btn" onClick={() => handleInlineInvite(sq.id, idx, false)}>
                                                                                <Send size={12} /> Invite
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                
                                                {/* Substitute slot */}
                                                <div className="roster-divider">Substitute (Optional)</div>
                                                {(() => {
                                                    const sub = entry.sub;
                                                    const hasSub = sq.members.some(m => m.role === 'substitute');
                                                    if (hasSub && (sub.state === 'accepted' || sub.state === 'pending')) return null;
                                                    return (
                                                        <div className={`roster-slot slot-${sub.state}`}>
                                                            <div className="slot-avatar">
                                                                {sub.state === 'empty' && (
                                                                    <button 
                                                                        type="button" 
                                                                        className={`slot-toggle-btn ${sub.showSearch ? 'is-open' : ''}`}
                                                                        onClick={() => {
                                                                            if (!sub.showSearch) {
                                                                                updateInlineSubSlot(sq.id, s => ({ ...s, showSearch: true, isSlidingOut: false }));
                                                                            } else {
                                                                                updateInlineSubSlot(sq.id, s => ({ ...s, isSlidingOut: true }));
                                                                                setTimeout(() => clearInlineSlot(sq.id, 0, true), 300);
                                                                            }
                                                                        }}
                                                                    >+</button>
                                                                )}
                                                                {sub.state === 'searching' && <Loader size={20} className="inline-spinner" />}
                                                                {(sub.state === 'available' || sub.state === 'pending') && <AvatarDisplay avatar={sub.photoURL || null} username={sub.username} size={28} />}
                                                                {(sub.state === 'unavailable' || sub.state === 'not-found') && <XCircle size={20} />}
                                                            </div>
                                                            <div className="slot-info">
                                                                <span className={`slot-label ${sub.showSearch ? 'slot-content-up' : ''}`}>🔄 Sub</span>
                                                                {sub.state === 'empty' && !sub.showSearch && <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>Optional — Click + to search</span>}
                                                                {sub.state === 'available' && <span className="slot-username slot-result-found" style={{ color: 'var(--success)' }}>{sub.username} ✓</span>}
                                                                {sub.state === 'not-found' && <span style={{ fontSize: '0.82rem', color: 'var(--danger)' }}>"{sub.searchInput}" not found</span>}
                                                                {(sub.showSearch || sub.state === 'not-found' || sub.state === 'unavailable' || sub.state === 'available') && sub.state !== 'searching' && (
                                                                    <div className={`inline-search-row ${sub.isSlidingOut ? 'sliding-out' : 'sliding-in'}`}>
                                                                        <input placeholder="Username" value={sub.searchInput} onChange={e => updateInlineSubSlot(sq.id, s => ({ ...s, searchInput: e.target.value, hasSearched: false }))} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleInlineSearch(sq.id, 0, true, sq.game); }}} />
                                                                        <button type="button" className={`search-btn ${sub.hasSearched ? 'is-cancel' : ''}`} onClick={() => { if (sub.hasSearched) { updateInlineSubSlot(sq.id, s => ({ ...s, isSlidingOut: true })); setTimeout(() => clearInlineSlot(sq.id, 0, true), 300); } else handleInlineSearch(sq.id, 0, true, sq.game); }}>
                                                                            {sub.hasSearched ? <X size={14} /> : <Search size={14} />}
                                                                        </button>
                                                                        {sub.state === 'available' && (
                                                                            <button type="button" className="invite-btn" onClick={() => handleInlineInvite(sq.id, 0, true)}><Send size={12} /> Invite</button>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        );
                                    })()}

                                    {/* Substitutes for ready squads */}
                                    {sq.status !== 'forming' && sq.members.filter(m => m.role === 'substitute').length > 0 && (
                                        <div>
                                            <strong style={{ display: 'block', marginBottom: 8, color: 'var(--text-secondary)' }}>Substitutes:</strong>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                {sq.members.filter(m => m.role === 'substitute').map((m, idx) => (
                                                    <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(145deg, rgba(255,255,255,0.05) 0%, rgba(0,0,0,0.2) 100%)', boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.05), 0 2px 4px rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.05)', padding: '8px 12px', borderRadius: 6 }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                            <AvatarDisplay avatar={m.photoURL || null} username={m.username} size={28} />
                                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                            <span title={m.status === 'accepted' ? 'Accepted' : 'Pending'} style={{ display: 'flex', alignItems: 'center', fontWeight: 700, color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.8)' }}>
                                                                {m.username} {m.status === 'accepted' ? <Check size={14} color="var(--success)" style={{ marginLeft: 6 }} /> : <Clock size={14} color="var(--warning)" style={{ marginLeft: 6 }} />}
                                                            </span>
                                                            <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>IGN: {m.ign}</span>
                                                            {m.gameId && <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>ID: {m.gameId}</span>}
                                                        </div>
                                                        </div>
                                                        <div>
                                                            {false ? (
                                                                <span className="status-badge online">🟢 Online</span>
                                                            ) : (
                                                                <span className="status-badge offline">⚪ Offline</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* IGNs */}
            <div className="card animate-in stagger-4">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={{ fontFamily: 'Outfit', fontWeight: 700 }}>🎮 In-Game Names</h3>
                    <button className="btn btn-primary btn-sm" onClick={() => setShowAddIgnModal(true)}>+ Add IGN</button>
                </div>
                {(p.igns || []).map((ign: any, i: number) => (
                    <div key={i} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '12px 16px', borderRadius: 'var(--radius-md)', marginBottom: 8,
                        background: 'var(--bg-glass)', border: '1px solid var(--border)',
                    }}>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                            <span className={`tc-game-badge ${ign.game === 'PUBG' ? 'pubg' : ign.game === 'Free Fire' ? 'freefire' : 'wildrift'}`}>{ign.game}</span>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <span style={{ fontWeight: 600 }}>{ign.name}</span>
                                {ign.idNumber && <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>ID: {ign.idNumber}</span>}
                            </div>
                        </div>
                        <button className="btn btn-secondary btn-sm" onClick={() => showAlert('✏️ Edit IGN modal will open here (Mock Mode)', 'Mock Feature')}><Edit size={13} /></button>
                    </div>
                ))}
                {(p.igns?.length || 0) < 3 && (
                    <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 8 }}>
                        You can add IGNs for games you haven't added yet.
                    </p>
                )}
            </div>

                        {/* Create Squad Modal — Simplified Phase 1 */}
            {showSquadModal && !editSquadId && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
                    <div className="card" style={{ width: '100%', maxWidth: 440, position: 'relative' }}>
                        <button style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }} onClick={() => setShowSquadModal(false)}><X size={20}/></button>
                        <h2 style={{ marginBottom: 6, fontFamily: 'Outfit' }}>Create New Squad</h2>
                        <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 20 }}>Pick a game and name your squad. You can invite members after creation.</p>
                        
                        <form onSubmit={handleSaveSquad} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <div className="mobile-col-stack" style={{ display: 'flex', gap: 12 }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', marginBottom: 6, fontSize: '0.82rem', fontWeight: 600 }}>Game</label>
                                    <select className="form-input" value={sqGame} onChange={e => handleGameChange(e.target.value as any)}>
                                        <option value="PUBG">PUBG (4+1)</option>
                                        <option value="Free Fire">Free Fire (4+1)</option>
                                        <option value="Wild Rift">Wild Rift (5+1)</option>
                                        <option value="MLBB">Mobile Legends (5+1)</option>
                                    </select>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', marginBottom: 6, fontSize: '0.82rem', fontWeight: 600 }}>Squad Name</label>
                                    <input className="form-input" required placeholder="e.g. Phoenix Rising" value={sqName} onChange={e => setSqName(e.target.value)} />
                                </div>
                            </div>

                            {/* Leader preview */}
                            <div className="roster-slot slot-accepted" style={{ opacity: 0.85 }}>
                                <div className="slot-avatar">👑</div>
                                <div className="slot-info">
                                    <span className="slot-label">{getSlotLabels(sqGame)[0]} — Leader</span>
                                    <span className="slot-username">{p.username}</span>
                                </div>
                                <span style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: 4, background: 'rgba(99,102,241,0.15)', color: 'var(--accent)' }}>You</span>
                            </div>

                            <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', textAlign: 'center', margin: '-4px 0' }}>
                                You'll be able to invite {getSlotCount(sqGame) - 1} teammates + 1 sub after creating the squad.
                            </p>

                            {isCreating ? (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px 0', color: 'var(--accent)' }}>
                                    <Loader size={18} className="inline-spinner" style={{ marginRight: 8 }} /> Creating squad...
                                </div>
                            ) : (
                                <button type="submit" className="btn btn-primary" style={{ marginTop: 4 }}>Create Squad</button>
                            )}
                        </form>
                    </div>
                </div>
            )}

            {/* Phase 4 Squad Settings Modal */}
            {showSquadModal && editSquadId && (() => {
                const sq = squads.find(s => s.id === editSquadId);
                if (!sq) return null;
                const isLeader = sq.members.some(m => m.uid === p.uid && m.role === 'leader');

                const entry = getOrInitInlineSlots(sq);
                const labels = getSlotLabels(sq.game);

                return (
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
                        <div className="card" style={{ width: '100%', maxWidth: 500, position: 'relative', maxHeight: '90vh', overflowY: 'auto' }}>
                            <button style={{ position: 'absolute', top: 16, right: 16, background: 'var(--bg-glass)', border: '1px solid var(--border)', color: 'var(--text-primary)', cursor: 'pointer', borderRadius: '50%', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowSquadModal(false)}><X size={16}/></button>
                            
                            <h2 style={{ marginBottom: 6, fontFamily: 'Outfit' }}>Squad Settings</h2>
                            <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 20 }}>Manage your squad name, co-leader, and roster.</p>

                            <div className="mobile-col-stack" style={{ display: 'flex', gap: 12, marginBottom: 24, paddingBottom: 24, borderBottom: '1px solid var(--border)' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', marginBottom: 6, fontSize: '0.82rem', fontWeight: 600 }}>Squad Name</label>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <input 
                                            className="form-input" 
                                            value={nameChangeInput !== '' ? nameChangeInput : sq.name} 
                                            onChange={e => setNameChangeInput(e.target.value)}
                                            disabled={!isLeader}
                                        />
                                        {isLeader && (
                                            <button 
                                                className="btn btn-primary" 
                                                onClick={() => handleSaveSquadName(sq, nameChangeInput !== '' ? nameChangeInput : sq.name)}
                                                style={{ whiteSpace: 'nowrap' }}
                                            >
                                                Save
                                            </button>
                                        )}
                                    </div>
                                    <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>Can be changed once every 120 days.</p>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', marginBottom: 6, fontSize: '0.82rem', fontWeight: 600 }}>Co-Leader</label>
                                    {isLeader ? (
                                        <select 
                                            className="form-input" 
                                            value={sq.members.find(m => m.role === 'co-leader')?.uid || ''}
                                            onChange={(e) => handleCoLeaderSelect(sq, e.target.value)}
                                        >
                                            <option value="">Select a member...</option>
                                            {sq.members.filter(m => m.role !== 'leader').map(m => (
                                                <option key={m.uid} value={m.uid}>{m.username}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span style={{ fontWeight: 600, fontSize: '1rem', display: 'block', marginTop: 4 }}>
                                            {sq.members.find(m => m.role === 'co-leader')?.username || 'None assigned'}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <h5 style={{ margin: '0 0 12px 0', color: 'var(--text-primary)', fontSize: '0.9rem' }}>Squad Roster</h5>
                            
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                                {/* Main Roster */}
                                {sq.members.filter(m => m.role !== 'substitute').map((m, idx) => (
                                    <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(145deg, rgba(255,255,255,0.05) 0%, rgba(0,0,0,0.2) 100%)', boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.05), 0 2px 4px rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.05)', padding: '8px 12px', borderRadius: 6 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                            <AvatarDisplay avatar={m.photoURL || null} username={m.username} size={28} />
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <span style={{ display: 'flex', alignItems: 'center', fontWeight: 700, color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.8)' }}>
                                                {m.username} {m.role === 'leader' && <span style={{ fontSize: '0.7rem', padding: '1px 4px', background: 'var(--accent)', color: 'white', borderRadius: 4, marginLeft: 6 }}>👑</span>}
                                                {m.role === 'co-leader' && <span style={{ fontSize: '0.7rem', padding: '1px 4px', background: 'var(--secondary)', color: 'white', borderRadius: 4, marginLeft: 6 }}>⭐</span>}
                                            </span>
                                            <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>IGN: {m.ign}</span>
                                            {m.gameId && <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>ID: {m.gameId}</span>}
                                        </div>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                            <PresenceBadge uid={m.uid} style={{ padding: '2px 4px', fontSize: '0.65rem' }} />
                                            
                                            {isLeader && m.uid !== p.uid && (
                                                <button onClick={() => handleKickMember(sq.id, m.username, m.uid)} className="btn btn-danger btn-sm" style={{ padding: '4px 8px', fontSize: '0.75rem' }}>Kick out</button>
                                            )}
                                            {!isLeader && m.uid === p.uid && (
                                                <button onClick={() => handleLeaveSquad(sq.id, sq.name)} className="btn btn-danger btn-sm" style={{ padding: '4px 8px', fontSize: '0.75rem' }}>Leave</button>
                                            )}
                                        </div>
                                    </div>
                                ))}

                                {/* Substitute Roster */}
                                {sq.members.some(m => m.role === 'substitute') && (
                                    <>
                                        <h6 style={{ margin: '8px 0 4px 0', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Substitute</h6>
                                        {sq.members.filter(m => m.role === 'substitute').map((m, idx) => (
                                            <div key={`sub-${idx}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(145deg, rgba(255,255,255,0.05) 0%, rgba(0,0,0,0.2) 100%)', boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.05), 0 2px 4px rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.05)', padding: '8px 12px', borderRadius: 6 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                    <AvatarDisplay avatar={m.photoURL || null} username={m.username} size={28} />
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <span style={{ display: 'flex', alignItems: 'center', fontWeight: 700, color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.8)' }}>
                                                        {m.username}
                                                    </span>
                                                    <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>IGN: {m.ign}</span>
                                                    {m.gameId && <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>ID: {m.gameId}</span>}
                                                </div>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                    <PresenceBadge uid={m.uid} style={{ padding: '2px 4px', fontSize: '0.65rem' }} />
                                                    
                                                    {isLeader && m.uid !== p.uid && (
                                                        <button onClick={() => handleKickMember(sq.id, m.username, m.uid)} className="btn btn-danger btn-sm" style={{ padding: '4px 8px', fontSize: '0.75rem' }}>Kick out</button>
                                                    )}
                                                    {!isLeader && m.uid === p.uid && (
                                                        <button onClick={() => handleLeaveSquad(sq.id, sq.name)} className="btn btn-danger btn-sm" style={{ padding: '4px 8px', fontSize: '0.75rem' }}>Leave</button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                )}
                            </div>

                            {/* Inline Invites section — uses exact same UI as expansion view */}
                            {isLeader && (() => {
                                const totalSlots = getSlotCount(sq.game);
                                const currentMembers = sq.members.filter(m => m.role !== 'substitute').length;
                                const hasSub = sq.members.some(m => m.role === 'substitute');
                                const hasOpenSlots = currentMembers < totalSlots || !hasSub;

                                return (
                                <div className="inline-roster" style={{ marginTop: 16 }}>
                                    {hasOpenSlots && (
                                        <button 
                                            type="button"
                                            className="btn btn-secondary" 
                                            style={{ width: '100%', marginBottom: showInviteSection ? 16 : 0 }}
                                            onClick={() => setShowInviteSection(!showInviteSection)}
                                        >
                                            {showInviteSection ? 'Hide Invite Section' : '+ Invite Member'}
                                        </button>
                                    )}
                                    
                                    {showInviteSection && hasOpenSlots && (
                                        <>
                                            <strong style={{ fontSize: '0.78rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Invite Members</strong>
                                    {entry.main.map((slot, idx) => {
                                        const existingNonLeader = sq.members.filter(m => m.role !== 'substitute' && m.role !== 'leader');
                                        if (idx < existingNonLeader.length && (slot.state === 'accepted' || slot.state === 'pending')) return null;
                                        
                                        const label = labels[idx + 1] || `Player ${idx + 2}`;
                                        return (
                                            <div key={`invite-${idx}`} className={`roster-slot slot-${slot.state}`}>
                                                <div className="slot-avatar">
                                                    {slot.state === 'empty' && (
                                                        <button 
                                                            type="button" 
                                                            className={`slot-toggle-btn ${slot.showSearch ? 'is-open' : ''}`}
                                                            onClick={() => {
                                                                if (!slot.showSearch) {
                                                                    updateInlineSlot(sq.id, idx, s => ({ ...s, showSearch: true, isSlidingOut: false }));
                                                                } else {
                                                                    updateInlineSlot(sq.id, idx, s => ({ ...s, isSlidingOut: true }));
                                                                    setTimeout(() => clearInlineSlot(sq.id, idx, false), 300);
                                                                }
                                                            }}
                                                        >+</button>
                                                    )}
                                                    {slot.state === 'searching' && <Loader size={20} className="inline-spinner" />}
                                                    {(slot.state === 'available' || slot.state === 'pending' || slot.state === 'accepted') && <AvatarDisplay avatar={slot.photoURL || null} username={slot.username} size={28} />}
                                                    {(slot.state === 'unavailable' || slot.state === 'not-found') && <XCircle size={20} />}
                                                </div>
                                                <div className="slot-info">
                                                    <span className={`slot-label ${slot.showSearch ? 'slot-content-up' : ''}`}>{label}</span>
                                                    {slot.state === 'empty' && !slot.showSearch && <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>Click + to search</span>}
                                                    {slot.state === 'searching' && <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>Searching...</span>}
                                                    {slot.state === 'not-found' && <span style={{ fontSize: '0.82rem', color: 'var(--danger)' }}>"{slot.searchInput}" not found</span>}
                                                    {slot.state === 'unavailable' && <span style={{ fontSize: '0.82rem', color: 'var(--danger)' }}>{slot.username} — in another squad</span>}
                                                    {slot.state === 'available' && <span className="slot-username slot-result-found" style={{ color: 'var(--success)' }}>{slot.username} ✓</span>}
                                                    {slot.state === 'pending' && <span className="slot-username" style={{ color: 'var(--warning)' }}>{slot.username} — Pending</span>}
                                                    
                                                    {/* Animated search row */}
                                                    {(slot.showSearch || slot.state === 'not-found' || slot.state === 'unavailable' || slot.state === 'available') && slot.state !== 'searching' && (
                                                        <div className={`inline-search-row ${slot.isSlidingOut ? 'sliding-out' : 'sliding-in'}`}>
                                                            <input 
                                                                placeholder="Username" 
                                                                value={slot.searchInput} 
                                                                onChange={e => updateInlineSlot(sq.id, idx, s => ({ ...s, searchInput: e.target.value, hasSearched: false }))} 
                                                                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleInlineSearch(sq.id, idx, false, sq.game); }}} 
                                                            />
                                                            <button 
                                                                type="button" 
                                                                className={`search-btn ${slot.hasSearched ? 'is-cancel' : ''}`} 
                                                                onClick={() => {
                                                                    if (slot.hasSearched) {
                                                                        updateInlineSlot(sq.id, idx, s => ({ ...s, isSlidingOut: true }));
                                                                        setTimeout(() => clearInlineSlot(sq.id, idx, false), 300);
                                                                    } else { handleInlineSearch(sq.id, idx, false, sq.game); }
                                                                }}
                                                            >
                                                                {slot.hasSearched ? <X size={14} /> : <Search size={14} />}
                                                            </button>
                                                            {slot.state === 'available' && (
                                                                <button type="button" className="invite-btn" onClick={() => handleInlineInvite(sq.id, idx, false)}>
                                                                    <Send size={12} /> Invite
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {/* Substitute slot */}
                                    <div className="roster-divider">Substitute (Optional)</div>
                                    {(() => {
                                        const sub = entry.sub;
                                        const hasSubMember = sq.members.some(m => m.role === 'substitute');
                                        if (hasSubMember && (sub.state === 'accepted' || sub.state === 'pending')) return null;
                                        return (
                                            <div className={`roster-slot slot-${sub.state}`}>
                                                <div className="slot-avatar">
                                                    {sub.state === 'empty' && (
                                                        <button 
                                                            type="button" 
                                                            className={`slot-toggle-btn ${sub.showSearch ? 'is-open' : ''}`}
                                                            onClick={() => {
                                                                if (!sub.showSearch) {
                                                                    updateInlineSubSlot(sq.id, s => ({ ...s, showSearch: true, isSlidingOut: false }));
                                                                } else {
                                                                    updateInlineSubSlot(sq.id, s => ({ ...s, isSlidingOut: true }));
                                                                    setTimeout(() => clearInlineSlot(sq.id, 0, true), 300);
                                                                }
                                                            }}
                                                        >+</button>
                                                    )}
                                                    {sub.state === 'searching' && <Loader size={20} className="inline-spinner" />}
                                                    {(sub.state === 'available' || sub.state === 'pending') && <AvatarDisplay avatar={sub.photoURL || null} username={sub.username} size={28} />}
                                                    {(sub.state === 'unavailable' || sub.state === 'not-found') && <XCircle size={20} />}
                                                </div>
                                                <div className="slot-info">
                                                    <span className={`slot-label ${sub.showSearch ? 'slot-content-up' : ''}`}>🔄 Sub</span>
                                                    {sub.state === 'empty' && !sub.showSearch && <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>Optional — Click + to search</span>}
                                                    {sub.state === 'available' && <span className="slot-username slot-result-found" style={{ color: 'var(--success)' }}>{sub.username} ✓</span>}
                                                    {sub.state === 'not-found' && <span style={{ fontSize: '0.82rem', color: 'var(--danger)' }}>"{sub.searchInput}" not found</span>}
                                                    {(sub.showSearch || sub.state === 'not-found' || sub.state === 'unavailable' || sub.state === 'available') && sub.state !== 'searching' && (
                                                        <div className={`inline-search-row ${sub.isSlidingOut ? 'sliding-out' : 'sliding-in'}`}>
                                                            <input placeholder="Username" value={sub.searchInput} onChange={e => updateInlineSubSlot(sq.id, s => ({ ...s, searchInput: e.target.value, hasSearched: false }))} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleInlineSearch(sq.id, 0, true, sq.game); }}} />
                                                            <button type="button" className={`search-btn ${sub.hasSearched ? 'is-cancel' : ''}`} onClick={() => { if (sub.hasSearched) { updateInlineSubSlot(sq.id, s => ({ ...s, isSlidingOut: true })); setTimeout(() => clearInlineSlot(sq.id, 0, true), 300); } else handleInlineSearch(sq.id, 0, true, sq.game); }}>
                                                                {sub.hasSearched ? <X size={14} /> : <Search size={14} />}
                                                            </button>
                                                            {sub.state === 'available' && (
                                                                <button type="button" className="invite-btn" onClick={() => handleInlineInvite(sq.id, 0, true)}><Send size={12} /> Invite</button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                        </>
                                    )}
                                </div>
                                );
                            })()}
                        </div>
                    </div>
                );
            })()}

            {/* Extra Modals for Co-leader assignment */}

            {coLeaderAssignTarget && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 20 }}>
                    <div className="card" style={{ width: '100%', maxWidth: 400 }}>
                        <h3 style={{ marginBottom: 12, fontFamily: 'Outfit', fontWeight: 700 }}>Assign Co-Leader</h3>
                        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: 20 }}>Are you sure you want to select <strong style={{ color: '#fff' }}>{coLeaderAssignTarget.member.username}</strong> as a co-leader? They will receive a notification.</p>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                            <button className="btn btn-ghost" onClick={() => setCoLeaderAssignTarget(null)}>Cancel</button>
                            <button className="btn btn-primary" onClick={confirmCoLeader}>Yes, select</button>
                        </div>
                    </div>
                </div>
            )}

            {coLeaderSuccessMsg && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 20 }}>
                    <div className="card" style={{ width: '100%', maxWidth: 400, textAlign: 'center' }}>
                        <h3 style={{ marginBottom: 12, fontFamily: 'Outfit', fontWeight: 700, color: 'var(--success)' }}>Success</h3>
                        <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: 24 }}>{coLeaderSuccessMsg}</p>
                        <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => setCoLeaderSuccessMsg(null)}>Close</button>
                    </div>
                </div>
            )}

            {/* Kick Out Modal */}
            {kickOutMember && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
                    <div className="card" style={{ width: '100%', maxWidth: 400 }}>
                        <h3 style={{ marginBottom: 12, fontFamily: 'Outfit', fontWeight: 700 }}>Kick out {kickOutMember.memberName}?</h3>
                        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: 16 }}>Please provide a reason. This player will receive a notification.</p>
                        <input className="form-input" value={kickOutReason} onChange={e => setKickOutReason(e.target.value)} style={{ width: '100%', marginBottom: 20 }} placeholder="Reason" />
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                            <button className="btn btn-ghost" style={{ border: '1px solid var(--border)' }} onClick={() => setKickOutMember(null)} disabled={isManagingMember}>Cancel</button>
                            <button className="btn btn-danger" onClick={confirmKickMember} disabled={isManagingMember}>
                                {isManagingMember ? 'Removing...' : 'Confirm Kick'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Leave Squad Modal */}
            {leaveSquad && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
                    <div className="card" style={{ width: '100%', maxWidth: 400 }}>
                        <h3 style={{ marginBottom: 12, fontFamily: 'Outfit', fontWeight: 700 }}>Leave {leaveSquad.squadName}?</h3>
                        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: 16 }}>Please provide a reason. The squad leader will be notified.</p>
                        <input className="form-input" value={leaveReason} onChange={e => setLeaveReason(e.target.value)} style={{ width: '100%', marginBottom: 20 }} placeholder="Reason for leaving" />
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                            <button className="btn btn-ghost" style={{ border: '1px solid var(--border)' }} onClick={() => setLeaveSquad(null)} disabled={isManagingMember}>Cancel</button>
                            <button className="btn btn-danger" onClick={confirmLeaveSquad} disabled={isManagingMember}>
                                {isManagingMember ? 'Leaving...' : 'Confirm Leave'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Avatar Picker Modal */}
            {showAvatarModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
                    <div className="card" style={{ width: '100%', maxWidth: 450, position: 'relative' }}>
                        <button style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }} onClick={() => {
                            setShowAvatarModal(false);
                            setSelectedImageForCrop(null);
                        }}><X size={20}/></button>
                        <h3 style={{ marginBottom: 24, fontFamily: 'Outfit', fontWeight: 700 }}>Profile Picture</h3>
                        
                        <div style={{ marginBottom: 24 }}>
                            {isUploadingAvatar ? (
                                <div style={{ textAlign: 'center', padding: '30px 0' }}>
                                    <Loader size={32} className="inline-spinner" style={{ marginBottom: 16, color: 'var(--primary)' }} />
                                    <p style={{ color: 'var(--text-secondary)' }}>Uploading... {Math.round(avatarUploadProgress)}%</p>
                                    <div style={{ width: '100%', height: 4, background: 'var(--bg-darker)', borderRadius: 2, marginTop: 12, overflow: 'hidden' }}>
                                        <div style={{ width: `${avatarUploadProgress}%`, height: '100%', background: 'var(--primary)', transition: 'width 0.2s' }} />
                                    </div>
                                </div>
                            ) : selectedImageForCrop ? (
                                /* ── Cropping UI ── */
                                <div style={{ width: '100%' }}>
                                    <div style={{ position: 'relative', width: '100%', height: 350, background: '#111', borderRadius: 8, overflow: 'hidden' }}>
                                        <Cropper
                                            image={selectedImageForCrop}
                                            crop={crop}
                                            zoom={zoom}
                                            aspect={1}
                                            cropShape="round"
                                            showGrid={false}
                                            onCropChange={setCrop}
                                            onZoomChange={setZoom}
                                            onCropComplete={handleCropComplete}
                                            style={{ containerStyle: { background: '#000' } }}
                                        />
                                    </div>
                                    <div style={{ padding: '20px 0 10px', display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Zoom:</span>
                                        <input
                                            type="range"
                                            value={zoom}
                                            min={1}
                                            max={3}
                                            step={0.1}
                                            onChange={(e) => setZoom(Number(e.target.value))}
                                            style={{ flex: 1, accentColor: 'var(--primary)' }}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                                        <button className="btn btn-ghost" style={{ flex: 1, border: '1px solid var(--border)' }} onClick={() => setSelectedImageForCrop(null)}>Back</button>
                                        <button className="btn btn-primary" style={{ flex: 1 }} onClick={uploadCroppedAvatar}>Apply & Save</button>
                                    </div>
                                </div>
                            ) : p.avatar && (p.avatar.startsWith('http') || p.avatar.startsWith('data:image')) ? (
                                /* ── Has image avatar — show preview + Change Avatar button ── */
                                <div style={{ display: 'flex', alignItems: 'center', gap: 20, justifyContent: 'center', padding: '16px 0' }}>
                                    <div style={{
                                        width: 100, height: 100, borderRadius: '50%', overflow: 'hidden',
                                        border: '3px solid var(--primary)', boxShadow: '0 0 20px rgba(139, 92, 246, 0.3)',
                                        flexShrink: 0,
                                    }}>
                                        <img 
                                            src={p.avatar} 
                                            alt="Current avatar"
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                    </div>
                                    <button
                                        onClick={() => fileInputRef.current?.click()}
                                        style={{
                                            background: 'linear-gradient(135deg, var(--primary), var(--primary-dark, #6d28d9))',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: 50,
                                            padding: '10px 22px',
                                            fontSize: '0.88rem',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            boxShadow: '0 4px 12px rgba(139, 92, 246, 0.35)',
                                            whiteSpace: 'nowrap',
                                        }}
                                        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(139, 92, 246, 0.5)'; }}
                                        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(139, 92, 246, 0.35)'; }}
                                    >
                                        Change Avatar
                                    </button>
                                    <input 
                                        type="file" 
                                        ref={fileInputRef}
                                        style={{ display: 'none' }} 
                                        accept="image/jpeg, image/png, image/webp" 
                                        onChange={onFileChange}
                                    />
                                </div>
                            ) : (
                                /* ── No image avatar — show upload area ── */
                                <div 
                                    style={{ border: '2px dashed var(--border)', borderRadius: 'var(--radius-lg)', padding: '30px 20px', textAlign: 'center', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, transition: 'all var(--t-fast)' }}
                                    onClick={() => fileInputRef.current?.click()}
                                    onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--primary)'}
                                    onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                                >
                                    <input 
                                        type="file" 
                                        ref={fileInputRef}
                                        style={{ display: 'none' }} 
                                        accept="image/jpeg, image/png, image/webp" 
                                        onChange={onFileChange}
                                    />
                                    <Upload size={32} style={{ color: 'var(--text-muted)' }} />
                                    <div>
                                        <p style={{ fontWeight: 600 }}>Click to upload an image</p>
                                        <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 4 }}>JPEG, PNG, WEBP (Max 5MB)</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div style={{ position: 'relative', marginTop: 30, marginBottom: 20 }}>
                            <div style={{ borderTop: '1px solid var(--border)', position: 'absolute', top: '50%', left: 0, right: 0 }}></div>
                            <div style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
                                <span style={{ background: 'var(--bg-glass)', padding: '0 10px', color: 'var(--text-muted)', fontSize: '0.85rem' }}>OR CHOOSE EMOJI</span>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8, opacity: isUploadingAvatar ? 0.5 : 1, pointerEvents: isUploadingAvatar ? 'none' : 'auto' }}>
                            {['👨', '👩', '🔫', '🗡️', '🛡️', '🎯'].map(emoji => (
                                <button key={emoji} onClick={() => { 
                                    updateUserContext(undefined, emoji);
                                    setShowAvatarModal(false); 
                                }} style={{ fontSize: '1.5rem', background: 'var(--bg-glass)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '10px 0', cursor: 'pointer', transition: 'all var(--t-fast)' }}>
                                    {emoji}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Personal Info Modal */}
            {showPersonalInfoModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
                    <div className="card" style={{ width: '100%', maxWidth: 450, position: 'relative' }}>
                        <button style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }} onClick={() => setShowPersonalInfoModal(false)}><X size={20}/></button>
                        <h3 style={{ marginBottom: 20, fontFamily: 'Outfit', fontWeight: 700 }}>Edit Personal Info</h3>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                                    <label style={{ fontSize: '0.85rem' }}>Username</label>
                                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Can be changed once every 120 days</span>
                                </div>
                                <input className="form-input" value={tempUsername} onChange={e => setTempUsername(e.target.value)} placeholder="Username" />
                            </div>
                            <div style={{ position: 'relative' }}>
                                <label style={{ display: 'block', marginBottom: 6, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Primary Email (Cannot be changed)</label>
                                <input className="form-input" disabled value={p.email} style={{ opacity: 0.6, cursor: 'not-allowed', width: '100%', position: 'relative', zIndex: 10 }} />
                                
                                <div className={`expand-clean ${!(showAddSecEmail || p.secondaryEmail) ? 'open' : ''}`}>
                                    <div>
                                        <button 
                                            className={`btn btn-ghost curtain-btn ${showAddSecEmail || p.secondaryEmail ? 'hidden' : ''}`} 
                                            style={{ color: '#00a8cc', padding: '0', textAlign: 'left', fontWeight: 600, marginTop: 4, position: 'relative', zIndex: 1, background: 'none', border: 'none' }} 
                                            onClick={(e) => { e.preventDefault(); setShowAddSecEmail(true); }}
                                        >
                                            + Add
                                        </button>
                                    </div>
                                </div>

                                <div className={`expand-clean ${showAddSecEmail || p.secondaryEmail ? 'open' : ''}`}>
                                    <div className={`curtain-input-wrapper ${showAddSecEmail || p.secondaryEmail ? 'open' : ''}`}>
                                        <div style={{ position: 'relative', marginTop: 12 }}>
                                            <input className="form-input" value={tempSecEmail} onChange={e => setTempSecEmail(e.target.value)} placeholder="Secondary Email" style={{ paddingRight: 32 }} />
                                            <button onClick={(e) => { e.preventDefault(); setTempSecEmail(''); setShowAddSecEmail(false); }} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: '#ef4444', color: 'white', border: 'none', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}><X size={12} /></button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div style={{ position: 'relative' }}>
                                <label style={{ display: 'block', marginBottom: 6, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Primary Phone {p.phone ? '(Cannot be changed)' : '(Please add your phone number)'}</label>
                                <input className="form-input" disabled={!!p.phone} value={p.phone ? p.phone : tempPrimaryPhone} onChange={e => setTempPrimaryPhone(e.target.value)} placeholder="01XXXXXXXXX" style={{ opacity: p.phone ? 0.6 : 1, cursor: p.phone ? 'not-allowed' : 'text', width: '100%', position: 'relative', zIndex: 10 }} />
                                
                                <div className={`expand-clean ${!(showAddSecPhone || p.secondaryPhone) ? 'open' : ''}`}>
                                    <div>
                                        <button 
                                            className={`btn btn-ghost curtain-btn ${showAddSecPhone || p.secondaryPhone ? 'hidden' : ''}`} 
                                            style={{ color: '#00a8cc', padding: '0', textAlign: 'left', fontWeight: 600, marginTop: 4, position: 'relative', zIndex: 1, background: 'none', border: 'none' }} 
                                            onClick={(e) => { e.preventDefault(); setShowAddSecPhone(true); }}
                                        >
                                            + Add
                                        </button>
                                    </div>
                                </div>

                                <div className={`expand-clean ${showAddSecPhone || p.secondaryPhone ? 'open' : ''}`}>
                                    <div className={`curtain-input-wrapper ${showAddSecPhone || p.secondaryPhone ? 'open' : ''}`}>
                                        <div style={{ position: 'relative', marginTop: 12 }}>
                                            <input className="form-input" value={tempSecPhone} onChange={e => setTempSecPhone(e.target.value)} placeholder="Secondary Phone" style={{ paddingRight: 32 }} />
                                            <button onClick={(e) => { e.preventDefault(); setTempSecPhone(''); setShowAddSecPhone(false); }} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: '#ef4444', color: 'white', border: 'none', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}><X size={12} /></button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <button className="btn btn-primary" onClick={async () => {
                                if (tempUsername !== p.username) {
                                    const lastChange = new Date(p.lastUsernameChange || 0);
                                    const diffTime = Math.abs(new Date().getTime() - lastChange.getTime());
                                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                                    if (diffDays < 120) {
                                        showAlert(`You can only change your username once every 120 days. You have ${120 - diffDays} days left.`, 'Username Limit');
                                        return;
                                    }
                                    const isUnique = await checkUsernameUnique(tempUsername, p.uid);
                                    if (!isUnique) {
                                        showAlert(`The username "${tempUsername}" is already taken. Please choose another one.`, 'Username Unavailable');
                                        return;
                                    }
                                }
                                
                                const updates: any = {
                                    username: tempUsername,
                                    secondaryEmail: tempSecEmail, 
                                    secondaryPhone: tempSecPhone,
                                };
                                if (tempUsername !== p.username) {
                                    updates.lastUsernameChange = new Date().toISOString();
                                }
                                if (!p.phone && tempPrimaryPhone.trim()) {
                                    updates.phone = tempPrimaryPhone.trim();
                                }
                                
                                try {
                                    await updateDoc(doc(db, 'users', p.uid), updates);

                                    if (tempUsername !== p.username) {
                                        updateUserContext(tempUsername, undefined);
                                        propagateUsernameChange(p.username, tempUsername, p.uid);
                                        showAlert('Profile updated successfully. All related squad data was synced globally.', 'Success');
                                    } else {
                                        showAlert('Profile updated successfully.', 'Success');
                                    }
                                    setShowPersonalInfoModal(false);
                                } catch (err: any) {
                                    console.error("Firestore update failed:", err);
                                    showAlert(`Database error: ${err.message}`, 'Update Failed');
                                }
                            }} style={{ marginTop: 10 }}>Save Changes</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add IGN Modal — Reusable Component */}
            <AddIgnModal
                isOpen={showAddIgnModal}
                onClose={() => setShowAddIgnModal(false)}
                existingGames={(p.igns || []).map((i: any) => i.game)}
                onSave={async (game, ignName, ignId) => {
                    const newIgns = [...(p.igns || []), { game, name: ignName, idNumber: ignId }];
                    await updateDoc(doc(db, 'users', p.uid), { igns: newIgns });
                    setShowAddIgnModal(false);
                }}
            />

            {/* Logout Button */}
            <div style={{ marginTop: 24, marginBottom: 40, display: 'flex', justifyContent: 'center' }}>
                <button 
                    className="btn btn-primary" 
                    style={{ background: 'var(--danger)', borderColor: 'var(--danger)', width: '100%', maxWidth: 300, display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'center' }}
                    onClick={async () => {
                        await logout();
                        navigate('/');
                    }}
                >
                    <LogOut size={20} />
                    Log Out
                </button>
            </div>
        </div>
    );
}
